// Source of truth: documentation/external/USER_GUIDE_CAMPAIGN_SIMULATOR.md
const CAMPAIGN_SIMULATOR_GUIDE = `# Campaign Simulator - User Guide

**Model marketing outcomes before you spend**

---

## What is the Campaign Simulator?

The Campaign Simulator helps you forecast how a marketing campaign might perform across channels over time. You configure business context, campaign budget, and channel mix, then run simulations to explore spend, leads, revenue, ROAS, and brand/SEO lift.

Two run modes:

- **Quick Run** — deterministic v1 timeline (formula + API when available)
- **Full Analysis** — Quick Run plus stochastic v2 panels (Monte Carlo bands, Markov journey, attribution, seasonality spectrum)

Results are exploratory — not guarantees of future performance.

---

## Getting Started

1. Open **Customer Portal → Campaign Simulator**
2. Fill in **Inputs** (business, campaign, channels)
3. Optionally expand **Advanced / Stochastic** for v2 settings
4. Click **Quick Run** or **Full Analysis**
5. Review **Outputs** and export CSV, JSON, or PDF

---

## Quick Run vs Full Analysis

| | Quick Run | Full Analysis |
|---|-----------|---------------|
| Speed | Faster | Slower (Monte Carlo sampling) |
| V1 dashboard | Yes | Yes |
| Probability cone | No | Yes |
| Journey flow | No | Yes |
| Attribution | No | Yes |
| Seasonality spectrum | No | Yes |
| Export v2 sections | Omitted / N/A | Included |

Use **Quick Run** to iterate on inputs. Use **Full Analysis** when you need uncertainty ranges and channel attribution under correlation stress.

---

## Inputs Glossary

### Business

| Field | What it affects |
|-------|-----------------|
| Industry / Geography | Context for AI dual-track insights |
| Monthly website traffic | SEO/organic baseline |
| Conversion rate | Lead and sales efficiency |
| Close rate | Lead → customer conversion |
| Average order value | Revenue per sale |
| Brand awareness / SEO score | Starting brand and SEO state |

### Campaign

| Field | What it affects |
|-------|-----------------|
| Total / monthly budget | Spend envelope over duration |
| Duration | Number of simulated periods |
| Competitiveness | Headwind on paid efficiency |
| Seasonality | Sine-shaped demand swing (-1 to 1) |
| Volatility | Random period noise amplitude |
| Creative quality | Paid channel CTR/lead boost |

### Channels

Enable channels and set budget share (should sum to 100% across enabled channels). Per-channel fields (CPC, CTR, lead rate, content frequency, etc.) tune how each channel contributes to impressions, clicks, leads, and revenue.

---

## Advanced / Stochastic Settings

These apply only to **Full Analysis** (engine v2).

| Setting | Purpose |
|---------|---------|
| **Seed** | Reproducible random draws |
| **Iterations** | Monte Carlo sample count (more = stabler bands, slower) |
| **Channel correlation** | How channels move together under stress (\`independent\`, \`moderate\`, \`crisis\`) |
| **Seasonality harmonics** | Fourier preset for seasonal multiplier (\`default\`, \`annual\`, \`rich\`) |
| **Adstock decay** | Carry-over of past spend effect per channel (auto = backend defaults) |

---

## Reading the Outputs

### V1 Dashboard

- **Metrics cards** — KPIs at the current playback step
- **Timeline chart** — cumulative spend, revenue, ROAS over the campaign
- **Channel impact** — spend vs revenue by channel at current step
- **Funnel** — impressions → clicks → leads → sales
- **Summary** — assumptions list and run metadata

Use **Play / Pause / Reset** and the step slider to scrub the timeline after a run.

### V2 Panels (Full Analysis)

| Panel | What it shows |
|-------|----------------|
| **Probability Cone** | Monthly p10–p90 band around median KPI |
| **Markov Journey Flow** | Particle flow across customer states |
| **Distribution Histogram** | Terminal KPI distribution across iterations |
| **Removal-Effect Attribution** | Credit vs spend share when each channel is removed |
| **Seasonality Spectrum** | Harmonic amplitudes + reconstructed seasonal curve |
| **Convergence** | Monte Carlo stderr tail — flat = stable sampler |

---

## Export Formats

Exports live at the top of the **Outputs** zone.

### JSON

Single structured payload: \`inputs\`, \`scenario\`, \`viewMode\`, \`stochasticControls\`, \`v1\` (timeline, summary, assumptions), and \`v2\` when Full Analysis completed.

### CSV

Sectioned file (Excel-friendly):

1. Run metadata (type, scenario, seed, iterations)
2. V1 monthly timeline
3. V2 percentile bands (p10/p50/p90 per month)
4. V2 attribution rows

### PDF

Report with campaign header, KPI table, condensed timeline, v2 percentiles and attribution when available. Footer links to this guide.

---

## Limitations & Assumptions

- **V1** uses simplified channel formulas; API path may use backend engine when deployed.
- **V2** is probabilistic — bands widen with volatility and correlation presets.
- Scenarios (\`expected\`, \`optimistic\`, \`pessimistic\`, \`monte_carlo\`) apply multipliers/noise to v1 playback, not a separate API per scenario in Quick Run.
- Neural trajectory overlay requires trained ONNX artifacts on the server; otherwise neutral fallback applies.
- Simulations do not replace live campaign data, incrementality tests, or financial sign-off.

---

## Need Help?

From the simulator page, click **How it works** or visit **Resources → Technical Docs → Campaign Simulator**.
`;

export default CAMPAIGN_SIMULATOR_GUIDE;
